import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type ReactNode,
} from 'react';
import {
  Box,
  FormHelperText,
  FormLabel,
  Stack,
  Typography,
  type BoxProps,
  type FormHelperTextProps,
  type FormLabelProps,
  type StackProps,
  type TypographyProps,
} from '@mui/material';
import { designTokens } from '../tokens';

type FieldContextValue = {
  controlId: string;
  labelId: string;
  messageId: string;
  required: boolean;
  disabled: boolean;
  readOnly: boolean;
  invalid: boolean;
};

const FieldContext = createContext<FieldContextValue | null>(null);

function useFieldContext(slot: string) {
  const context = useContext(FieldContext);
  if (!context) throw new Error(`Field.${slot} must be used inside Field.Root.`);
  return context;
}

function normalizeId(id: string) {
  return id.replace(/[^a-zA-Z0-9_-]/g, '');
}

export type FieldRootProps = Omit<StackProps, 'component'> & {
  /** 연결할 실제 control의 id입니다. 생략하면 Field가 안정적인 id를 생성합니다. */
  controlId?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  invalid?: boolean;
};

const FieldRoot = forwardRef<HTMLDivElement, FieldRootProps>(function FieldRoot(
  {
    id,
    controlId,
    required = false,
    disabled = false,
    readOnly = false,
    invalid = false,
    spacing = 0.5,
    children,
    ...stackProps
  },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? `field-${normalizeId(generatedId)}`;
  const resolvedControlId = controlId ?? `${fieldId}-control`;
  const context: FieldContextValue = {
    controlId: resolvedControlId,
    labelId: `${resolvedControlId}-label`,
    messageId: `${resolvedControlId}-message`,
    required,
    disabled,
    readOnly,
    invalid,
  };

  return (
    <FieldContext.Provider value={context}>
      <Stack
        {...stackProps}
        ref={ref}
        id={fieldId}
        spacing={spacing}
        data-disabled={disabled || undefined}
        data-invalid={invalid || undefined}
        data-readonly={readOnly || undefined}
        data-required={required || undefined}
      >
        {children}
      </Stack>
    </FieldContext.Provider>
  );
});

export type FieldHeaderProps = Omit<StackProps, 'component'>;

const FieldHeader = forwardRef<HTMLDivElement, FieldHeaderProps>(function FieldHeader(
  {
    direction = 'row',
    spacing = 1,
    useFlexGap = true,
    sx,
    ...stackProps
  },
  ref,
) {
  const sxOverrides = Array.isArray(sx) ? sx : sx ? [sx] : [];

  return (
    <Stack
      {...stackProps}
      ref={ref}
      direction={direction}
      spacing={spacing}
      useFlexGap={useFlexGap}
      sx={[{ alignItems: 'baseline', justifyContent: 'space-between' }, ...sxOverrides]}
    />
  );
});

export type FieldLabelProps = Omit<
  FormLabelProps,
  'component' | 'disabled' | 'error' | 'htmlFor' | 'id' | 'required'
>;

const FieldLabel = forwardRef<HTMLLabelElement, FieldLabelProps>(function FieldLabel(
  { sx, ...labelProps },
  ref,
) {
  const { controlId, labelId, required, disabled, invalid } = useFieldContext('Label');
  const sxOverrides = Array.isArray(sx) ? sx : sx ? [sx] : [];

  return (
    <FormLabel
      {...labelProps}
      ref={ref}
      component="label"
      id={labelId}
      htmlFor={controlId}
      required={required}
      error={invalid}
      disabled={disabled}
      sx={[
        {
          minWidth: 0,
          color: invalid ? designTokens.color.dangerText : designTokens.color.primaryText,
          ...designTokens.typography.ui,
          fontWeight: 500,
          '&.Mui-focused': {
            color: invalid ? designTokens.color.dangerText : designTokens.color.primaryText,
          },
          '& .MuiFormLabel-asterisk': {
            color: invalid ? designTokens.color.dangerText : designTokens.color.secondaryText,
          },
          '&.Mui-disabled': {
            color: designTokens.color.primaryText,
            opacity: designTokens.button.disabledOpacity,
          },
        },
        ...sxOverrides,
      ]}
    />
  );
});

export type FieldSuffixProps = Omit<BoxProps, 'component'>;

const FieldSuffix = forwardRef<HTMLDivElement, FieldSuffixProps>(function FieldSuffix(
  { sx, ...boxProps },
  ref,
) {
  const { disabled } = useFieldContext('Suffix');
  const sxOverrides = Array.isArray(sx) ? sx : sx ? [sx] : [];

  return (
    <Box
      {...boxProps}
      ref={ref}
      sx={[
        {
          flexShrink: 0,
          color: disabled ? designTokens.color.disabledText : designTokens.color.secondaryText,
          ...designTokens.typography.supporting,
        },
        ...sxOverrides,
      ]}
    />
  );
});

export type FieldControlState = Readonly<FieldContextValue>;

export type FieldControlProps = Omit<BoxProps, 'children' | 'component'> & {
  /**
   * MUI control을 그대로 전달하거나, 생성된 id를 control의 slot에 연결해야 할 때 render 함수를
   * 사용할 수 있습니다. Field는 입력 동작이나 값을 소유하지 않습니다.
   */
  children: ReactNode | ((state: FieldControlState) => ReactNode);
};

const FieldControl = forwardRef<HTMLDivElement, FieldControlProps>(function FieldControl(
  { children, sx, ...boxProps },
  ref,
) {
  const context = useFieldContext('Control');
  const sxOverrides = Array.isArray(sx) ? sx : sx ? [sx] : [];

  return (
    <Box {...boxProps} ref={ref} sx={[{ minWidth: 0 }, ...sxOverrides]}>
      {typeof children === 'function' ? children(context) : children}
    </Box>
  );
});

export type FieldFooterProps = Omit<StackProps, 'component'>;

const FieldFooter = forwardRef<HTMLDivElement, FieldFooterProps>(function FieldFooter(
  {
    direction = 'row',
    spacing = 1,
    useFlexGap = true,
    sx,
    ...stackProps
  },
  ref,
) {
  const sxOverrides = Array.isArray(sx) ? sx : sx ? [sx] : [];

  return (
    <Stack
      {...stackProps}
      ref={ref}
      direction={direction}
      spacing={spacing}
      useFlexGap={useFlexGap}
      sx={[{ alignItems: 'flex-start', justifyContent: 'space-between' }, ...sxOverrides]}
    />
  );
});

export type FieldMessageProps = Omit<
  FormHelperTextProps,
  'component' | 'disabled' | 'error' | 'id' | 'required'
>;

const FieldMessage = forwardRef<HTMLParagraphElement, FieldMessageProps>(function FieldMessage(
  { sx, ...messageProps },
  ref,
) {
  const { messageId, required, disabled, invalid } = useFieldContext('Message');
  const sxOverrides = Array.isArray(sx) ? sx : sx ? [sx] : [];

  return (
    <FormHelperText
      {...messageProps}
      ref={ref}
      id={messageId}
      required={required}
      error={invalid}
      disabled={disabled}
      sx={[
        {
          flex: 1,
          minWidth: 0,
          m: 0,
          color: invalid ? designTokens.color.dangerText : designTokens.color.secondaryText,
          ...designTokens.typography.supporting,
        },
        ...sxOverrides,
      ]}
    />
  );
});

export type FieldCountProps = Omit<TypographyProps, 'component'> & {
  /** 글자 수 등 보조 값이 허용 범위를 벗어났는지 표시합니다. */
  invalid?: boolean;
};

const FieldCount = forwardRef<HTMLElement, FieldCountProps>(function FieldCount(
  { sx, variant = 'caption', invalid = false, ...typographyProps },
  ref,
) {
  const { disabled } = useFieldContext('Count');
  const sxOverrides = Array.isArray(sx) ? sx : sx ? [sx] : [];

  return (
    <Typography
      {...typographyProps}
      ref={ref}
      variant={variant}
      sx={[
        {
          flexShrink: 0,
          color: disabled ? designTokens.color.disabledText : invalid ? designTokens.color.dangerText : designTokens.color.secondaryText,
          ...designTokens.typography.supporting,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        },
        ...sxOverrides,
      ]}
    />
  );
});

/**
 * MUI input을 대체하지 않고 label, control, 도움말과 보조 정보를 같은 구조로 조합하는 field anatomy입니다.
 */
export const Field = {
  Root: FieldRoot,
  Header: FieldHeader,
  Label: FieldLabel,
  Suffix: FieldSuffix,
  Control: FieldControl,
  Footer: FieldFooter,
  Message: FieldMessage,
  Count: FieldCount,
} as const;
